import { useMemo } from "react";
import { useLang } from "@/i18n/LangContext";
import { getLocaleString, formatSteamPrice as fmtSteamPrice, formatCurrencyByLang } from "@/lib/format";
import type {
  AppDetail,
  ReviewSummary,
  StoreLow,
  PriceDeal,
  HistoryPoint,
} from "@/types/api";

interface FreeGameInfo {
  store: string;
  endDate?: string | null;
}

interface GameAnalysisProps {
  app: AppDetail;
  playerCount: number | null;
  reviewSummary: ReviewSummary | null;
  storeLow: StoreLow | null;
  historyLow: HistoryPoint[];
  priceDeals: PriceDeal[];
  steamSpyOwners: string;
  steamSpyAvgPlaytime: number;
  freeGameInfo?: FreeGameInfo | null;
}

/* ── helpers ── */

function parseOwnerMidpoint(owners: string): number {
  const nums = owners.replace(/,/g, "").match(/\d+/g);
  if (!nums || nums.length < 2) return 0;
  return Math.round((Number(nums[0]) + Number(nums[1])) / 2);
}

function formatCount(n: number, lang: string): string {
  return n.toLocaleString(getLocaleString(lang));
}

/** Steam cents -> formatted price (delegated to central format.ts) */
function formatSteamPrice(steamCents: number, lang: string, _currency?: string): string {
  return fmtSteamPrice(steamCents, lang);
}

/** ITAD display-ready amount -> formatted price (delegated to central format.ts) */
function formatItadPrice(displayAmount: number, lang: string, _currency?: string): string {
  return formatCurrencyByLang(displayAmount, lang);
}

function computeSaleFrequency(history: HistoryPoint[]): { months: number; tier: "frequent" | "regular" | "occasional" | "rare" } | null {
  const drops = history.filter((p) => p.cut > 0);
  if (drops.length < 2) return null;
  const timestamps = drops.map((d) => new Date(d.timestamp).getTime()).sort((a, b) => a - b);
  const spanMs = timestamps[timestamps.length - 1] - timestamps[0];
  const spanMonths = spanMs / (1000 * 60 * 60 * 24 * 30);
  if (spanMonths < 1) return null;
  const avg = Math.round((spanMonths / (drops.length - 1)) * 10) / 10;
  const tier = avg < 2 ? "frequent" : avg < 4 ? "regular" : avg < 6 ? "occasional" : "rare";
  return { months: avg, tier };
}

function isRecentOrUpcoming(app: AppDetail): boolean {
  if (app.release_date?.coming_soon) return true;
  const dateStr = app.release_date?.date;
  if (!dateStr) return false;
  // Steam date formats: "Mar 19, 2026", "2026년 3월 19일" etc.
  const nums = dateStr.match(/\d+/g);
  if (!nums || nums.length < 3) return false;
  // Use 4-digit number as year
  const yearStr = nums.find((n) => n.length === 4);
  if (!yearStr) return false;
  const year = Number(yearStr);
  const rest = nums.filter((n) => n !== yearStr).map(Number);
  const month = Math.min(...rest);
  const day = Math.max(...rest);
  const released = new Date(year, month - 1, day);
  if (isNaN(released.getTime())) return false;
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  return released >= threeMonthsAgo;
}

function computePriceFairness(
  app: AppDetail,
  storeLow: StoreLow | null,
  saleFreq: ReturnType<typeof computeSaleFrequency>,
): number {
  if (app.is_free) return 5;
  const discount = app.price_overview?.discount_percent ?? 0;
  if (discount >= 75) return 5;
  if (storeLow && app.price_overview) {
    const current = app.price_overview.final / 100; // Steam cents -> display
    const low = storeLow.price.amount; // ITAD display-ready
    if (low > 0 && current <= low * 1.1) return 4.5;
  }
  if (discount >= 50) return 4;
  if (discount >= 25) return 3;
  if (discount > 0) return 2.5;
  if (saleFreq?.tier === "frequent") return 2;
  return 1.5;
}

function computeReviewScore(summary: ReviewSummary | null): number {
  if (!summary || summary.total_reviews === 0) return 0;
  const pct = (summary.total_positive / summary.total_reviews) * 100;
  if (pct >= 95) return 5;
  if (pct >= 85) return 4.5;
  if (pct >= 75) return 4;
  if (pct >= 65) return 3;
  if (pct >= 50) return 2;
  return 1;
}

function computePopularityScore(owners: number): number {
  if (owners > 5_000_000) return 5;
  if (owners > 1_000_000) return 4;
  if (owners > 200_000) return 3;
  if (owners > 50_000) return 2;
  return 1;
}

/* ── star SVGs ── */

function StarFull() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-amber-400">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function StarHalf() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" className="text-amber-400">
      <defs>
        <linearGradient id="half">
          <stop offset="50%" stopColor="currentColor" />
          <stop offset="50%" stopColor="#d1d5db" />
        </linearGradient>
      </defs>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" fill="url(#half)" />
    </svg>
  );
}

function StarEmpty() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-gray-300 dark:text-gray-600">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function Stars({ score }: { score: number }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (score >= i) stars.push(<StarFull key={i} />);
    else if (score >= i - 0.5) stars.push(<StarHalf key={i} />);
    else stars.push(<StarEmpty key={i} />);
  }
  return <div className="flex gap-0.5">{stars}</div>;
}

/* ── section wrapper ── */

function Section({ id, icon, title, children }: { id: string; icon?: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section aria-labelledby={id} className="border-t border-gray-100 dark:border-card-border pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
      <div className="flex items-center gap-2.5 mb-3">
        {icon && (
          <div className="w-7 h-7 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center shrink-0">
            {icon}
          </div>
        )}
        <h3 id={id} className="text-base font-bold text-gray-900 dark:text-white">{title}</h3>
      </div>
      <div className={`text-sm text-gray-700 dark:text-foreground leading-relaxed space-y-1.5 ${icon ? "pl-[38px]" : ""}`}>
        {children}
      </div>
    </section>
  );
}

/* ── main component ── */

export default function GameAnalysis({
  app,
  playerCount,
  reviewSummary,
  storeLow,
  historyLow,
  priceDeals,
  steamSpyOwners,
  steamSpyAvgPlaytime,
  freeGameInfo,
}: GameAnalysisProps) {
  const { lang, t } = useLang();
  const a = (t.gameDetail as Record<string, unknown>).analysis as Record<string, string>;

  const analysis = useMemo(() => {
    const ownerCount = parseOwnerMidpoint(steamSpyOwners);
    const saleFreq = computeSaleFrequency(historyLow);

    /* ─ price ─ */
    let priceTexts: string[] | null = null;
    const cur = app.price_overview?.currency ?? storeLow?.price?.currency ?? "KRW";
    // Check if any store currently offers this game for free (ITAD deals or Epic free-games)
    const freeStores = priceDeals.filter((d) => d.price.amount === 0);
    const hasFreeOffer = freeStores.length > 0 || freeGameInfo;
    if (app.is_free) {
      priceTexts = [a.freeGame];
    } else if (hasFreeOffer) {
      priceTexts = [];
      if (freeGameInfo) {
        priceTexts.push(a.freeAtStore.replace("{stores}", freeGameInfo.store));
      } else {
        const storeNames = freeStores.map((d) => d.shop.name).join(", ");
        priceTexts.push(a.freeAtStore.replace("{stores}", storeNames));
      }
      if (app.price_overview) {
        const fmtCurrent = formatSteamPrice(app.price_overview.final, lang, cur);
        priceTexts.push(a.steamPrice.replace("{price}", fmtCurrent));
      }
    } else if (app.price_overview || storeLow) {
      priceTexts = [];
      const discount = app.price_overview?.discount_percent ?? 0;
      if (discount > 0) {
        priceTexts.push(a.currentDiscount.replace("{percent}", String(discount)));
      }
      if (storeLow && app.price_overview) {
        const currentPriceSteam = app.price_overview.final; // Steam cents
        const lowPriceItad = storeLow.price.amount; // ITAD display-ready (KRW 22500=W22,500, USD 21=$21)
        const itadCur = storeLow.price.currency || cur;
        const fmtCurrent = formatSteamPrice(currentPriceSteam, lang, cur);
        const fmtLow = formatItadPrice(lowPriceItad, lang, itadCur);
        // Compare in same unit (display): Steam cents / 100, ITAD already display
        const currentDisplay = currentPriceSteam / 100;
        const lowDisplay = lowPriceItad;
        // Historic low was free or nearly free (KRW <=100won, USD/EUR <=$1)
        const nearlyFreeThreshold = (itadCur === "KRW" || itadCur === "JPY" || itadCur === "VND") ? 100 : 1;
        const wasNearlyFree = lowDisplay <= 0 || lowDisplay < nearlyFreeThreshold;
        if (wasNearlyFree) {
          priceTexts.push(a.currentVsLowFree.replace("{lowPrice}", fmtLow).replace("{currentPrice}", fmtCurrent));
        } else if (currentDisplay <= lowDisplay) {
          const isBelow = currentDisplay < lowDisplay * 0.95;
          priceTexts.push((isBelow ? a.belowHistoricLow : a.atHistoricLow).replace("{currentPrice}", fmtCurrent).replace("{lowPrice}", fmtLow));
        } else {
          priceTexts.push(a.currentVsLow.replace("{currentPrice}", fmtCurrent).replace("{lowPrice}", fmtLow));
        }
      }
      if (saleFreq) {
        const key = `saleFreq${saleFreq.tier.charAt(0).toUpperCase() + saleFreq.tier.slice(1)}` as keyof typeof a;
        priceTexts.push((a[key] as string).replace("{months}", String(saleFreq.months)));
      } else if (historyLow.length < 2) {
        priceTexts.push(a.saleFreqNoData);
      }
      // recommendation
      if (discount >= 75) priceTexts.push(a.exceptionalDeal);
      else if (discount >= 50) priceTexts.push(a.greatDeal);
      else if (storeLow && app.price_overview && storeLow.price.amount > 0) {
        const diff = ((app.price_overview.final / 100 - storeLow.price.amount) / storeLow.price.amount) * 100;
        if (diff <= 10) priceTexts.push(a.buyNow);
        else if (discount === 0 && saleFreq?.tier === "frequent") priceTexts.push(a.waitForSale);
        else if (discount === 0 && (!saleFreq || saleFreq.tier === "rare")) priceTexts.push(a.considerBuying);
      }
    }

    /* ─ reviews ─ */
    let reviewTexts: string[] | null = null;
    if (reviewSummary && reviewSummary.total_reviews > 0) {
      reviewTexts = [];
      const pct = Math.round((reviewSummary.total_positive / reviewSummary.total_reviews) * 100);
      reviewTexts.push(a.reviewPositive.replace("{percent}", String(pct)).replace("{total}", formatCount(reviewSummary.total_reviews, lang)));
      if (reviewSummary.total_reviews < 50) {
        reviewTexts.push(a.reviewFewReviews.replace("{total}", formatCount(reviewSummary.total_reviews, lang)));
      } else if (reviewSummary.total_reviews >= 500) {
        reviewTexts.push(a.reviewHighConfidence.replace("{total}", formatCount(reviewSummary.total_reviews, lang)));
      }
      if (pct >= 95) reviewTexts.push(a.sentimentExcellent);
      else if (pct >= 80) reviewTexts.push(a.sentimentVeryGood);
      else if (pct >= 65) reviewTexts.push(a.sentimentGood);
      else if (pct >= 40) reviewTexts.push(a.sentimentMixed);
      else reviewTexts.push(a.sentimentNegative);
    }

    /* ─ popularity ─ */
    let popularityTexts: string[] | null = null;
    if (playerCount !== null || ownerCount > 0 || steamSpyAvgPlaytime > 0) {
      popularityTexts = [];
      if (playerCount !== null) {
        const fc = formatCount(playerCount, lang);
        if (playerCount > 100_000) popularityTexts.push(a.playersExtreme.replace("{count}", fc));
        else if (playerCount > 10_000) popularityTexts.push(a.playersVeryActive.replace("{count}", fc));
        else if (playerCount > 1_000) popularityTexts.push(a.playersActive.replace("{count}", fc));
        else if (playerCount > 100) popularityTexts.push(a.playersModerate.replace("{count}", fc));
        else popularityTexts.push(a.playersLow.replace("{count}", fc));
      }
      if (ownerCount > 0) {
        const fo = formatCount(ownerCount, lang);
        if (ownerCount > 5_000_000) popularityTexts.push(a.ownersVeryPopular.replace("{owners}", fo));
        else if (ownerCount > 1_000_000) popularityTexts.push(a.ownersPopular.replace("{owners}", fo));
        else if (ownerCount > 200_000) popularityTexts.push(a.ownersModerate.replace("{owners}", fo));
        else popularityTexts.push(a.ownersNiche.replace("{owners}", fo));
      }
      if (steamSpyAvgPlaytime > 0) {
        const hours = Math.round(steamSpyAvgPlaytime / 60);
        const fh = String(hours);
        if (hours >= 20) popularityTexts.push(a.playtimeHigh.replace("{hours}", fh));
        else if (hours >= 5) popularityTexts.push(a.playtimeModerate.replace("{hours}", fh));
        else popularityTexts.push(a.playtimeShort.replace("{hours}", fh));
      }
    }

    /* ─ overall score ─ */
    const priceFairness = computePriceFairness(app, storeLow, saleFreq);
    const reviewScore = computeReviewScore(reviewSummary);
    const popScore = computePopularityScore(ownerCount);

    const hasReview = reviewSummary && reviewSummary.total_reviews >= 10;
    const recent = isRecentOrUpcoming(app);
    const hasPrice = !recent && !!(app.is_free || app.price_overview);
    const hasPop = ownerCount > 0;

    // Need at least 10 reviews for overall rating
    let finalScore = 0;
    if (hasReview) {
      let overall = 0;
      let totalWeight = 0;
      if (hasPrice) { overall += priceFairness * 0.35; totalWeight += 0.35; }
      overall += reviewScore * 0.45; totalWeight += 0.45;
      if (hasPop) { overall += popScore * 0.20; totalWeight += 0.20; }
      finalScore = totalWeight > 0 ? Math.round((overall / totalWeight) * 2) / 2 : 0;
    }

    let overallText = "";
    if (finalScore >= 4.5) overallText = a.overallExcellent;
    else if (finalScore >= 3.5) overallText = a.overallGood;
    else if (finalScore >= 2.5) overallText = a.overallCaution;
    else if (finalScore >= 1.5) overallText = a.overallWait;
    else if (finalScore > 0) overallText = a.overallNotRecommended;

    const insufficientData = !hasReview && reviewSummary !== null && reviewSummary.total_reviews > 0 && reviewSummary.total_reviews < 10;

    return { priceTexts, reviewTexts, popularityTexts, finalScore, overallText, insufficientData, reviewCount: reviewSummary?.total_reviews ?? 0 };
  }, [app, playerCount, reviewSummary, storeLow, historyLow, priceDeals, steamSpyOwners, steamSpyAvgPlaytime, freeGameInfo, lang, a]);

  // No data at all -> don't render
  if (!analysis.priceTexts && !analysis.reviewTexts && !analysis.popularityTexts) return null;

  return (
    <article
      aria-label={a.title}
      className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-6 mb-6"
    >
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M9 17H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-4" />
            <polyline points="12 15 12 22" />
            <polyline points="8 22 16 22" />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{a.title}</h2>
      </div>

      {/* Price Analysis */}
      {analysis.priceTexts ? (
        <Section
          id="analysis-price"
          title={a.priceTitle}
          icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
          }
        >
          {analysis.priceTexts.map((text, i) => (
            <p key={i}>{text}</p>
          ))}
        </Section>
      ) : (
        <Section id="analysis-price" title={a.priceTitle} icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>}>
          <p className="text-gray-400 dark:text-muted">{a.noPriceData}</p>
        </Section>
      )}

      {/* Review Analysis */}
      {analysis.reviewTexts ? (
        <Section
          id="analysis-review"
          title={a.reviewTitle}
          icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          }
        >
          {analysis.reviewTexts.map((text, i) => (
            <p key={i}>{text}</p>
          ))}
        </Section>
      ) : (
        <Section id="analysis-review" title={a.reviewTitle} icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>}>
          <p className="text-gray-400 dark:text-muted">{a.noReviewData}</p>
        </Section>
      )}

      {/* Popularity Analysis */}
      {analysis.popularityTexts ? (
        <Section
          id="analysis-popularity"
          title={a.popularityTitle}
          icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
            </svg>
          }
        >
          {analysis.popularityTexts.map((text, i) => (
            <p key={i}>{text}</p>
          ))}
        </Section>
      ) : (
        <Section id="analysis-popularity" title={a.popularityTitle} icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>}>
          <p className="text-gray-400 dark:text-muted">{a.noPlayerData}</p>
        </Section>
      )}

      {/* Overall Rating */}
      {analysis.finalScore > 0 ? (
        <Section
          id="analysis-overall"
          title={a.overallTitle}
          icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          }
        >
          <div className="flex items-center gap-3">
            <div role="img" aria-label={`${analysis.finalScore} ${a.outOf}`}>
              <Stars score={analysis.finalScore} />
            </div>
            <span className="text-xl font-black text-gray-900 dark:text-white tabular-nums">
              {analysis.finalScore} <span className="text-sm font-medium text-gray-400 dark:text-muted">{a.outOf}</span>
            </span>
          </div>
          <p className="font-semibold text-gray-800 dark:text-gray-200 mt-1">{analysis.overallText}</p>
          <p className="text-xs text-gray-400 dark:text-muted mt-0.5">{a.basedOn}</p>
        </Section>
      ) : analysis.insufficientData ? (
        <Section
          id="analysis-overall"
          title={a.overallTitle}
          icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          }
        >
          <p className="text-gray-500 dark:text-muted">{a.insufficientReviews.replace("{count}", String(analysis.reviewCount)).replace("{required}", "10")}</p>
        </Section>
      ) : null}

      {/* disclaimer */}
      <p className="text-xs text-gray-400 dark:text-muted mt-4 pt-3 border-t border-gray-100 dark:border-card-border">
        {a.dataDisclaimer}
      </p>
    </article>
  );
}
